<script lang="ts" setup>
    import { mdiAlertBoxOutline, mdiAlphaFBoxOutline, mdiAlphaUBoxOutline } from '@mdi/js';
    import type { Instance as TippyInstance } from 'tippy.js';
    import tippy from 'tippy.js';
    import { AlertStatusId, SystemAlertStatusId } from '@/constants';
    import { useSystemStatusStore } from '@/stores/systemStatus';
    import { useUserStore } from '@/stores/user';

    const { t } = useI18n();
    const systemStatusStore = useSystemStatusStore();
    const userStore = useUserStore();
    const router = useRouter();

    const goToAppAlerts = (): void => {
        router.push({ name: 'appAlerts' });
    };

    const goToAlerts = (): void => {
        router.push({ name: 'alerts' });
    };

    const goToMonitoring = (): void => {
        router.push({ name: 'monitoring' });
    };

    const goToUsersAwaitingDocumentApproval = (): void => {
        router.push({
            name: 'toolUserDetails',
            query: { userDocumentStatus: 'approve' },
        });
    };

    const systemAlertColor = (statusId: number | undefined): string => {
        switch (statusId) {
            case SystemAlertStatusId.Success:
                return 'tbGreen';
            case SystemAlertStatusId.Warning:
                return 'tbYellow';
            case SystemAlertStatusId.Fail:
                return 'tbRed';
            default:
                return 'grey';
        }
    };

    const alertStatusColor = (statusId: number | undefined): string => {
        switch (statusId) {
            case AlertStatusId.Success:
                return 'tbGreen';
            case AlertStatusId.Fail:
                return 'tbRed';
            default:
                return 'grey';
        }
    };

    const userDocumentsColor = computed<string>(() => {
        if (!systemStatusStore.userDocumentsStatus) return 'grey';
        return systemStatusStore.userDocumentsStatus.docCount > 0 ? 'tbYellow' : 'tbGreen';
    });

    const userDocumentsTooltip = computed<string>(() => {
        if (!systemStatusStore.userDocumentsStatus) return '';
        return t('menu.alertsUserDocuments', { count: systemStatusStore.userDocumentsStatus.docCount });
    });

    const appAlertsIconColor = computed<string>(() => systemAlertColor(systemStatusStore.appAlerts?.statusId));
    const alertsIconColor = computed<string>(() => alertStatusColor(systemStatusStore.alerts?.statusId));
    const monitoringIconColor = computed<string>(() => systemAlertColor(systemStatusStore.monitoring?.statusId));

    const btnAppAlerts = ref<{ $el: HTMLElement } | null>(null);
    const btnUserDocs = ref<{ $el: HTMLElement } | null>(null);
    const btnAlerts = ref<{ $el: HTMLElement } | null>(null);
    const btnMonitoring = ref<{ $el: HTMLElement } | null>(null);

    const tippyMap = new Map<string, TippyInstance>();

    // Long-press tooltip on touch devices.
    // Tippy's show() bails out when currentInput.isTouch && !props.touch,
    // so forceShow temporarily flips props.touch before the call.
    // isProgrammaticShow blocks tippy-internal hides (synthetic mouseleave/click
    // after touchend) while allowing our explicit hideAll() calls.
    const LONG_PRESS_MS = 500;
    let longPressTimer: ReturnType<typeof setTimeout> | null = null;
    let isLongPress = false;
    let isProgrammaticShow = false;
    const alertBtnEls: HTMLElement[] = [];

    const createTippyOptions = (getContent: () => string) => ({
        allowHTML: true,
        placement: 'bottom' as const,
        theme: 'light',
        trigger: 'mouseenter' as const,
        touch: false as const,
        delay: [700, 0] as [number, number],
        maxWidth: 400,
        content: '',
        popperOptions: {
            strategy: 'fixed' as const,
            modifiers: [{ name: 'flip', options: { fallbackPlacements: [] } }],
        },
        onShow(instance: TippyInstance) {
            const content = getContent();
            if (!content) return false as const;

            const isDark = localStorage.hasOwnProperty('isDark') ? JSON.parse(localStorage.getItem('isDark') as string) : false;
            instance.popper.children[0].classList.remove('v-theme--light', 'v-theme--dark');
            instance.popper.children[0].classList.add(isDark ? 'v-theme--dark' : 'v-theme--light');

            instance.setContent(content);
        },
        onHide() {
            if (isProgrammaticShow) return false as const;
        },
    });

    const forceShow = (instance: TippyInstance) => {
        const orig = instance.props.touch;
        instance.props.touch = true;
        isProgrammaticShow = true;
        instance.show();
        instance.props.touch = orig;
    };

    const hideAll = () => {
        isProgrammaticShow = false;
        tippyMap.forEach((inst) => inst.hide());
    };

    const onTouchStart = (key: string) => {
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            longPressTimer = null;
        }

        const wasVisible = tippyMap.get(key)?.state.isVisible ?? false;
        hideAll();
        isLongPress = false;

        if (wasVisible) return;

        longPressTimer = setTimeout(() => {
            isLongPress = true;
            const inst = tippyMap.get(key);
            if (inst) forceShow(inst);
        }, LONG_PRESS_MS);
    };

    const onTouchEnd = () => {
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            longPressTimer = null;
        }
    };

    const onTouchMove = () => {
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            longPressTimer = null;
        }
        hideAll();
        isLongPress = false;
    };

    const onDocumentTouch = (e: TouchEvent) => {
        if (!isProgrammaticShow) return;
        const target = e.target as Node;
        if (!alertBtnEls.some((el) => el.contains(target))) hideAll();
    };

    const handleNavClick = (action: () => void) => {
        if (isLongPress) {
            isLongPress = false;
            return;
        }
        action();
    };

    onMounted((): void => {
        const config: Array<[string, typeof btnAppAlerts, () => string]> = [
            ['appAlerts', btnAppAlerts, () => systemStatusStore.appAlerts?.message || ''],
            ['userDocs', btnUserDocs, () => userDocumentsTooltip.value],
            ['alerts', btnAlerts, () => systemStatusStore.alerts?.message || ''],
            ['monitoring', btnMonitoring, () => systemStatusStore.monitoring?.message || ''],
        ];

        config.forEach(([key, btnRef, getContent]) => {
            const el = btnRef.value?.$el;
            if (el) {
                alertBtnEls.push(el);
                tippyMap.set(key, tippy(el, createTippyOptions(getContent)));
                el.addEventListener('touchstart', () => onTouchStart(key), { passive: true });
                el.addEventListener('touchend', onTouchEnd, { passive: true });
                el.addEventListener('touchmove', onTouchMove, { passive: true });
            }
        });

        document.addEventListener('touchstart', onDocumentTouch, { passive: true });
    });

    onBeforeUnmount((): void => {
        if (longPressTimer) clearTimeout(longPressTimer);
        document.removeEventListener('touchstart', onDocumentTouch);
        tippyMap.forEach((instance) => instance.destroy());
        tippyMap.clear();
        alertBtnEls.length = 0;
    });
</script>

<template>
    <div class="d-flex align-center" @contextmenu.prevent>
        <!-- !!! only for root (not admin) -->
        <v-btn
            v-if="userStore.isRoot()"
            ref="btnAppAlerts"
            class="alert-btn mx-1"
            height="24"
            width="24"
            variant="text"
            :rounded="2"
            @click="handleNavClick(goToAppAlerts)"
        >
            <v-icon :icon="mdiAlertOctagonOutline" class="alert-btn__icon" :class="`alert-btn__icon--${appAlertsIconColor}`" />
        </v-btn>

        <!-- U, !, F only for admin (admin includes root) -->
        <template v-if="userStore.isAdmin()">
            <v-btn
                ref="btnUserDocs"
                class="alert-btn mx-1"
                height="24"
                width="24"
                variant="text"
                :rounded="2"
                @click="handleNavClick(goToUsersAwaitingDocumentApproval)"
            >
                <v-icon :icon="mdiAlphaUBoxOutline" class="alert-btn__icon" :class="`alert-btn__icon--${userDocumentsColor}`" />
            </v-btn>

            <v-btn ref="btnAlerts" class="alert-btn mx-1" height="24" width="24" variant="text" :rounded="2" @click="handleNavClick(goToAlerts)">
                <v-icon :icon="mdiAlertBoxOutline" class="alert-btn__icon" :class="`alert-btn__icon--${alertsIconColor}`" />
            </v-btn>

            <v-btn ref="btnMonitoring" class="alert-btn mx-1" height="24" width="24" variant="text" :rounded="2" @click="handleNavClick(goToMonitoring)">
                <v-icon :icon="mdiAlphaFBoxOutline" class="alert-btn__icon" :class="`alert-btn__icon--${monitoringIconColor}`" />
            </v-btn>
        </template>
    </div>
</template>

<style lang="scss" scoped>
    .alert-btn {
        padding: 0;
        min-width: 24px !important;
        max-width: 24px !important;
        user-select: none;
        -webkit-user-select: none;
        -webkit-touch-callout: none;

        &__icon {
            font-size: 24px !important;

            &--tbGreen {
                color: rgb(var(--v-theme-tbGreen)) !important;
            }

            &--tbRed {
                color: rgb(var(--v-theme-tbRed)) !important;
            }

            &--tbYellow {
                color: rgb(var(--v-theme-tbYellow)) !important;
            }

            &--grey {
                color: rgb(var(--v-theme-primary)) !important;
            }
        }
    }
</style>
